/**
 * Hooks for admin user actions (password reset, email verification, etc.)
 */

import { useState, useCallback } from "react";
import { apiPost, apiDelete } from "../lib/api";

// ----- Types -----

interface ActionResponse {
  success: boolean;
  message: string;
}

interface ActionResult {
  success: boolean;
  message: string;
  error?: string;
}

// ----- useAdminAction Hook -----

/**
 * Generic hook for admin actions that returns loading state and execute function.
 */
function useAdminAction<TBody = void>(
  endpoint: (profileId: string) => string,
  method: "POST" | "DELETE" = "POST"
) {
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(
    async (profileId: string, body?: TBody): Promise<ActionResult> => {
      setIsLoading(true);
      try {
        const url = endpoint(profileId);
        const result =
          method === "DELETE"
            ? await apiDelete<ActionResponse>(url)
            : await apiPost<ActionResponse, TBody | Record<string, never>>(
                url,
                body ?? ({} as TBody)
              );

        if (result.error) {
          return {
            success: false,
            message: result.error.message,
            error: result.error.message,
          };
        }

        return {
          success: result.data?.success ?? true,
          message: result.data?.message ?? "Action completed",
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "An error occurred";
        return {
          success: false,
          message,
          error: message,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [endpoint, method]
  );

  return { execute, isLoading };
}

// ----- Specific Action Hooks -----

/**
 * Hook for resetting a user's password.
 */
export function useResetPassword() {
  return useAdminAction((profileId) => `/api/admin/users/${profileId}/reset-password`);
}

/**
 * Hook for resending email verification.
 */
export function useResendVerification() {
  return useAdminAction((profileId) => `/api/admin/users/${profileId}/resend-verification`);
}

/**
 * Hook for manually verifying a user's email.
 */
export function useVerifyEmail() {
  return useAdminAction((profileId) => `/api/admin/users/${profileId}/verify-email`);
}

/**
 * Hook for applying credit to a user's account.
 */
export function useApplyCredit() {
  return useAdminAction<{ amountCents: number; reason: string }>(
    (profileId) => `/api/admin/users/${profileId}/apply-credit`
  );
}

/**
 * Hook for canceling a user's subscription.
 */
export function useCancelSubscription() {
  return useAdminAction<{ reason: string; immediately: boolean }>(
    (profileId) => `/api/admin/users/${profileId}/cancel-subscription`
  );
}

/**
 * Hook for deleting a user's account.
 */
export function useDeleteAccount() {
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(
    async (
      profileId: string,
      body: { reason: string; hardDelete: boolean }
    ): Promise<ActionResult> => {
      setIsLoading(true);
      try {
        // DELETE requests with body need special handling
        const result = await apiDelete<ActionResponse>(
          `/api/admin/users/${profileId}`
        );

        // Since apiDelete doesn't support body, we need to use fetch directly
        const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
        const token = await getAccessToken();

        const response = await fetch(`${API_URL}/api/admin/users/${profileId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });

        const json = await response.json();

        if (!response.ok) {
          return {
            success: false,
            message: json.error?.message ?? "Failed to delete account",
            error: json.error?.message,
          };
        }

        return {
          success: json.data?.success ?? true,
          message: json.data?.message ?? "Account deleted",
        };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "An error occurred";
        return {
          success: false,
          message,
          error: message,
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { execute, isLoading };
}

// Helper to get access token
async function getAccessToken(): Promise<string | null> {
  try {
    const { supabase } = await import("../lib/supabase");
    if (!supabase) return null;
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

// ----- Combined Hook -----

/**
 * Combined hook that provides all admin actions.
 */
export function useAdminActions() {
  const resetPassword = useResetPassword();
  const resendVerification = useResendVerification();
  const verifyEmail = useVerifyEmail();
  const applyCredit = useApplyCredit();
  const cancelSubscription = useCancelSubscription();
  const deleteAccount = useDeleteAccount();

  return {
    resetPassword,
    resendVerification,
    verifyEmail,
    applyCredit,
    cancelSubscription,
    deleteAccount,
  };
}

export default useAdminActions;
